/**
 * A property value as it goes over the wire: scalars pass through, `vector`
 * `[X, Y, Z]`, `rotator` `[Pitch, Yaw, Roll]` and `color` `[R, G, B(, A)]`
 * properties are number arrays — the same shape the transform fields use.
 */
export type RealtimeEntityPropertyValue = string | number | boolean | number[];

/**
 * The persistable snapshot of one entity — what a Save stores and what the
 * saved-state comparison runs against. Mirrors the backend's save body.
 */
export interface IRealtimeEntitySnapshot {
  entityId: string;
  type: string;
  name: string;
  position?: number[];
  rotation?: number[];
  scale?: number[];
  properties?: Record<string, RealtimeEntityPropertyValue>;
}

/**
 * One entity spawned live over the WebSocket, as the hub tracks it.
 *
 * Live state is engine-side and per-session; only what the author explicitly
 * saved persists on the backend, and `saved` carries that last snapshot so the
 * registry can tell a saved entity from a session-only or edited one.
 */
export interface IRealtimeEntity {
  /** Wire id the entity was spawned with; the engine tracks it only by this. */
  entityId: string;
  /** `typeId` from the build's `spawnableObjects` schema. */
  typeId: string;
  /** Schema `displayName` at spawn time, kept for list labels. */
  displayName: string;
  /**
   * Author-given label, hub-side only — the wire contract has no name field,
   * so this never reaches the engine. Defaults to `displayName` plus a counter.
   */
  name: string;
  /** `[X, Y, Z]`, Unreal units. */
  position?: number[];
  /** `[Pitch, Yaw, Roll]`, degrees. */
  rotation?: number[];
  /** `[X, Y, Z]` per-axis factors. */
  scale?: number[];
  properties?: Record<string, RealtimeEntityPropertyValue>;
  /**
   * The last snapshot saved to the backend, `null`/absent while the entity is
   * session-only. Dirty = the current state no longer matches it.
   */
  saved?: IRealtimeEntitySnapshot | null;
  /**
   * Set once the engine reported the spawn; hub-side only, it is what lets
   * the editor persist the entity.
   */
  engineReported?: boolean;
  /**
   * Configuration open at spawn time, the only one the post-report persist
   * writes into; hub-side only.
   */
  brandingId?: string;
}

/**
 * Tells whether an engine report applies to an entity: only a never-saved one
 * does, since a saved one would only receive float32 copies of its own values.
 *
 * @param entities - The session registry.
 * @param entityId - Entity the report names.
 * @returns Whether the registry holds the entity as never saved.
 */
export const takesEngineReport = (
  entities: IRealtimeEntity[],
  entityId: string,
): boolean => {
  const existing = entities.find((entity) => entity.entityId === entityId);

  return !!existing && !existing.saved;
};

/**
 * Picks the reported values the entity has no value for yet.
 *
 * The hub's value wins over the engine's echo of a class default, since the
 * engine applies the hub's edit after reporting; omitted keys are left alone.
 *
 * @param entity - The tracked entity.
 * @param properties - The reported values, already validated.
 * @returns The subset to merge — the keys the form had nothing for.
 */
export const reportedPropertiesToApply = (
  entity: IRealtimeEntity,
  properties: Record<string, RealtimeEntityPropertyValue>,
): Record<string, RealtimeEntityPropertyValue> =>
  Object.fromEntries(
    Object.entries(properties).filter(
      ([name]) => entity.properties?.[name] === undefined,
    ),
  );

/**
 * Applies the engine's report to a hub-spawned entity: fills the values the
 * form had nothing for and marks it reported, with an empty bag too.
 *
 * @param entities - The session registry.
 * @param entityId - Entity the report names.
 * @param properties - The reported values, already validated; empty when the
 * report carried none.
 * @returns The updated registry, or `null` when the report applies to nothing.
 */
export const applyEngineReport = (
  entities: IRealtimeEntity[],
  entityId: string,
  properties: Record<string, RealtimeEntityPropertyValue>,
): IRealtimeEntity[] | null => {
  if (!takesEngineReport(entities, entityId)) {
    return null;
  }

  return entities.map((entity) => {
    if (entity.entityId !== entityId) {
      return entity;
    }

    const applied = reportedPropertiesToApply(entity, properties);

    return {
      ...entity,
      ...(Object.keys(applied).length
        ? { properties: { ...entity.properties, ...applied } }
        : {}),
      engineReported: true,
    };
  });
};

/**
 * Builds the persistable snapshot of an entity's current state.
 *
 * @param entity - Tracked entity from the session registry.
 * @returns The snapshot a save sends and the dirty check compares against.
 */
export const buildRealtimeEntitySnapshot = (
  entity: IRealtimeEntity,
): IRealtimeEntitySnapshot => ({
  entityId: entity.entityId,
  type: entity.typeId,
  name: entity.name,
  ...(entity.position ? { position: entity.position } : {}),
  ...(entity.rotation ? { rotation: entity.rotation } : {}),
  ...(entity.scale ? { scale: entity.scale } : {}),
  ...(entity.properties && Object.keys(entity.properties).length
    ? { properties: entity.properties }
    : {}),
});

/**
 * Sorts a plain object's keys for serialisation, so equal snapshots serialise
 * alike whatever order their bags were assembled in.
 *
 * @param _key - Property being serialised; unused.
 * @param value - Value at that property.
 * @returns The value, with a plain object's keys sorted.
 */
const sortSnapshotKeys = (_key: string, value: unknown): unknown =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(
        Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
          a.localeCompare(b),
        ),
      )
    : value;

/**
 * Tells whether an entity's current state diverged from its last save — or was
 * never saved at all.
 *
 * Key order is ignored: the backend returns the property bag in storage order.
 *
 * @param entity - Tracked entity from the session registry.
 * @returns True while the entity has unsaved changes.
 */
export const isRealtimeEntityDirty = (entity: IRealtimeEntity): boolean => {
  if (!entity.saved) {
    return true;
  }
  return (
    JSON.stringify(buildRealtimeEntitySnapshot(entity), sortSnapshotKeys) !==
    JSON.stringify(entity.saved, sortSnapshotKeys)
  );
};

/**
 * Rebuilds a registry entry from a snapshot the backend returned.
 *
 * `saved` is derived from the entry rather than copied from the input, so a
 * just-loaded entity is clean by construction — projecting the two separately
 * is what made a stored empty property bag read as forever dirty.
 *
 * @param snapshot - Saved snapshot as the backend stores it.
 * @returns The registry entry, marked as matching its save.
 */
export const realtimeEntityFromSnapshot = (
  snapshot: IRealtimeEntitySnapshot,
): IRealtimeEntity => {
  const entity: IRealtimeEntity = {
    entityId: snapshot.entityId,
    typeId: snapshot.type,
    // The label came from the build schema at spawn time and is not stored;
    // the tree shows `name` anyway, so the type id is label enough here.
    displayName: snapshot.type,
    name: snapshot.name,
    ...(snapshot.position ? { position: snapshot.position } : {}),
    ...(snapshot.rotation ? { rotation: snapshot.rotation } : {}),
    ...(snapshot.scale ? { scale: snapshot.scale } : {}),
    ...(snapshot.properties ? { properties: snapshot.properties } : {}),
  };

  return { ...entity, saved: buildRealtimeEntitySnapshot(entity) };
};
